import { useCallback, useEffect, useState } from 'react';

import { configureFields, loadCollectJS } from 'utils/collectJS';
import { getLogger } from 'utils/logger';

const logger = getLogger('useCollectJS');

/**
 * Loads Collect.js (on first use) and configures the inline card/bank fields.
 * Mounting a component that calls this hook is what pulls the payment script
 * into the page — it is not loaded anywhere else.
 *
 * A load failure (commonly an ad-blocker silently dropping the request to
 * secure.safewebservices.com) used to only be logged: `isReady` stayed false
 * forever with no way for a caller to tell "still loading" from "never
 * going to load", so the gated sites showed a dead disabled button with
 * nothing in the UI explaining why. `error` and `retry` let a caller render
 * an actionable failure state instead.
 *
 * @returns {{isReady: boolean, error: Error|null, retry: () => void}}
 *   `isReady` — whether Collect.js has been configured for these fields.
 *   `error` — the load rejection, or null while loading/once ready.
 *   `retry` — re-runs the load (e.g. after the user disables an ad-blocker).
 */
export default function useCollectJS(
	onFinish,
	onValidate,
	fields,
	dependencies,
) {
	const [isReady, setIsReady] = useState(false);
	const [error, setError] = useState(null);
	const [retryCount, setRetryCount] = useState(0);

	useEffect(() => {
		let isCurrent = true;

		setError(null);

		loadCollectJS()
			.then((collectJS) => {
				// A dependency changed while the script was loading; the effect that
				// replaced this one configures the fields instead.
				if (!isCurrent) return;

				configureFields(collectJS, {
					styleSniffer: true,
					callback: (resp) => {
						onFinish(resp);
					},
					fields,
					validationCallback: onValidate,
				});
				setIsReady(true);
			})
			.catch((loadError) => {
				logger.error(loadError.message);
				if (!isCurrent) return;

				setIsReady(false);
				setError(loadError);
			});

		return () => {
			isCurrent = false;
		};
	}, [...dependencies, retryCount]);

	const retry = useCallback(() => setRetryCount((count) => count + 1), []);

	return { isReady, error, retry };
}
